
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { JournalEntry, RawJournalEntry, SortOption } from "./types";

export const useJournalEntries = () => {
  const [entries, setEntries] = useState<JournalEntry[]>([]);
  const [filteredEntries, setFilteredEntries] = useState<JournalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('newest');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const { toast } = useToast();

  useEffect(() => {
    fetchEntries();
  }, []);

  useEffect(() => {
    let filtered = [...entries];
    
    if (searchTerm) {
      filtered = filtered.filter(entry => 
        entry.title.toLowerCase().includes(searchTerm.toLowerCase()) || 
        entry.notes.text.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    filtered.sort((a, b) => {
      switch (sortOption) {
        case 'newest':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
        case 'oldest':
          return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
        case 'mostLiked':
          return b.likes - a.likes;
        default:
          return 0;
      }
    });
    
    setFilteredEntries(filtered);
  }, [entries, searchTerm, sortOption]);

  const fetchEntries = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('recipe_journals')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      const transformedEntries = data.map((entry: RawJournalEntry) => ({
        ...entry,
        notes: JSON.parse(entry.notes || '{"text":"", "images":[]}') as { text: string, images: string[] }
      }));
      
      setEntries(transformedEntries);
      setFilteredEntries(transformedEntries);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load journal entries",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleLike = async (id: string) => {
    try {
      const { error } = await supabase
        .from('recipe_journals')
        .update({ likes: entries.find(e => e.id === id)?.likes! + 1 })
        .eq('id', id);

      if (error) throw error;

      setEntries(entries.map(entry =>
        entry.id === id ? { ...entry, likes: entry.likes + 1 } : entry
      ));
      
      toast({
        title: "❤️ Liked!",
        description: "You liked this cooking memory",
        duration: 2000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to like entry",
        variant: "destructive",
      });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('recipe_journals')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setEntries(entries.filter(entry => entry.id !== id));
      toast({
        title: "Success",
        description: "Journal entry deleted"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete entry",
        variant: "destructive",
      });
    }
  };

  return {
    filteredEntries,
    loading,
    searchTerm,
    setSearchTerm,
    sortOption,
    setSortOption,
    viewMode,
    setViewMode,
    handleLike,
    handleDelete,
  };
};
